import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { AccountMovementAttachmentDTO } from '../../dto/CurrentAccountDTO';
import { useCurrentAccountService } from '../../servicesHooks/CurrentAccount/useCurrentAccountService';
import { parseApiError } from '../../services/apiError';
import useNotify from '../../hooks/useNotify';
import { formatDateOnly } from '../../utils/date';

interface MovementAttachmentsDialogProps {
    accountMovementId: number | null;
    /** Número del comprobante del movimiento, para saber a qué comprobante se le adjunta el archivo. */
    formNumber: string | null;
    onHide: () => void;
}

const ACCEPTED_FILES = 'image/*,application/pdf,.pdf,.jpg,.jpeg,.png,.webp';

function fileIcon(mimetype: string | null): string {
    if (mimetype?.startsWith('image/')) return 'pi pi-image';
    if (mimetype === 'application/pdf') return 'pi pi-file-pdf';
    return 'pi pi-file';
}

export default function MovementAttachmentsDialog({ accountMovementId, formNumber, onHide }: MovementAttachmentsDialogProps) {
    const { getMovementAttachments, uploadMovementAttachment } = useCurrentAccountService();
    const notify = useNotify();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [attachments, setAttachments] = useState<AccountMovementAttachmentDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (accountMovementId == null) {
            setAttachments([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        getMovementAttachments(accountMovementId)
            .then((data: AccountMovementAttachmentDTO[]) => { if (!cancelled) setAttachments(data ?? []); })
            .catch((error: unknown) => {
                parseApiError(error);
                if (cancelled) return;
                setAttachments([]);
                notify.error('No se pudieron cargar los comprobantes adjuntos. Intentá de nuevo.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [accountMovementId, notify]);

    async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file || accountMovementId == null) return;
        setUploading(true);
        try {
            const uploaded: AccountMovementAttachmentDTO = await uploadMovementAttachment(accountMovementId, file);
            setAttachments(prev => [...prev, uploaded]);
            notify.success('Comprobante adjuntado');
        } catch (error: unknown) {
            parseApiError(error);
            notify.error('No se pudo subir el archivo. Intentá de nuevo.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    }

    return (
        <Dialog
            header="Comprobantes adjuntos"
            visible={accountMovementId != null}
            onHide={() => { if (!uploading) onHide(); }}
            breakpoints={{ '640px': '100vw' }}
            className="nv-scope nv-dialog nv-dialog-s"
            modal
        >
            <div className="nv-dialog-body">
                <div className="nv-section-head">
                    <div className="nv-ro">
                        <span className="nv-ro-label">Comprobante del movimiento</span>
                        <span className="nv-ro-value nv-strong">{formNumber || 'Sin número'}</span>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept={ACCEPTED_FILES}
                        onChange={handleFileSelected}
                    />
                    <Button
                        type="button"
                        className="buttonMain2"
                        outlined
                        icon="pi pi-upload"
                        label="Subir comprobante"
                        loading={uploading}
                        onClick={() => fileInputRef.current?.click()}
                    />
                </div>

                <DataTable
                    value={attachments}
                    dataKey="accountmovementattachment"
                    loading={loading}
                    className="nv-load-table nv-compact"
                    emptyMessage="Todavía no hay comprobantes adjuntos."
                >
                    <Column
                        header="Archivo"
                        body={(att: AccountMovementAttachmentDTO) => (
                            <span className="nv-row-8">
                                <i className={fileIcon(att.mimetype)} aria-hidden="true" />
                                <span className="nv-truncate" title={att.filename}>{att.filename}</span>
                            </span>
                        )}
                    />
                    <Column header="Subido" className="nv-col-date" body={(att: AccountMovementAttachmentDTO) => formatDateOnly(att.uploadedat)} />
                    <Column
                        className="nv-col-actions"
                        body={(att: AccountMovementAttachmentDTO) => att.publicurl && (
                            <Button
                                type="button"
                                text
                                className="nv-icon"
                                icon="pi pi-eye"
                                aria-label={`Ver ${att.filename}`}
                                tooltip="Ver archivo"
                                tooltipOptions={{ event: 'both', position: 'left', className: 'nv-tooltip' }}
                                onClick={() => window.open(att.publicurl ?? undefined, '_blank', 'noopener,noreferrer')}
                            />
                        )}
                    />
                </DataTable>
            </div>
        </Dialog>
    );
}
